import { useEffect, useMemo, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { getMedicalDutys } from "~/services/medicalcompany/dutys";
import PrepaymentFormData from "~/store/PrepaymentFormData.js";
import DutyLine, { isDutyAvailable } from "~/features/doctorForm/DutyLine";
import { SectionLabel } from "~/features/doctorForm/FormLayout";
import {
  countAvailable,
  defaultMonth,
  dutiesInMonth,
  groupByWeek,
  monthBounds,
  monthName,
  monthShort,
  monthsInWindow,
  shortDate,
  splitByWindow,
  sumValues,
  todayIso,
  windowRange,
} from "~/features/doctorForm/period";
import { formatBRL } from "~/shared/ui/format";

const UNAVAILABLE = "Vaga indisponível para antecipação. Entre em contato com os escalistas para regularizar o valor deste plantão.";
const OUT_OF_RANGE = "Plantão fora do período filtrado. Não disponível para antecipação.";
const ONLY_CONCLUDED_HINT = "Só aparecem plantões já encerrados (com check-out ou terminados há mais de 6 horas).";
const plantoes = (n) => `${n} ${n === 1 ? "plantão" : "plantões"}`;
const monthOf = (duty) => String(duty.date).slice(0, 7);

// Plantões fora da janela (antes dela ou depois de hoje): aparecem esmaecidos e não selecionáveis.
const OutOfRange = ({ title, duties, mobile, checkboxFor }) => (
  <Box component="section" aria-label={title} sx={{ mt: 3 }}>
    <SectionLabel>
      {`${title} `}
      <Box component="span" sx={{ color: "text.secondary", fontWeight: 500, fontSize: 14 }}>
        (fora do período · {duties.length})
      </Box>
    </SectionLabel>
    <Paper variant="outlined" sx={{ mt: 1, borderRadius: 2, overflow: "hidden" }}>
      {duties.map((duty, i) => (
        <Box key={duty.id} sx={{ borderTop: i ? "1px solid #eceef1" : "none" }}>
          <DutyLine dateFirst duty={duty} mobile={mobile} muted action={checkboxFor(duty, true)} note={OUT_OF_RANGE} />
        </Box>
      ))}
    </Paper>
  </Box>
);

// Passo 4, variação B "Por mês" (G1.10): uma busca da janela inteira, cartões de mês, lista por semana.
// `onlyConcluded` = flag formOnlyConcluded (regra 2b): o BE já filtra; o FE reforça e explica a regra.
const Step4 = ({ setDisabledNext, setActiveStep, mobile, today, onlyConcluded = false }) => {
  const range = useMemo(() => windowRange(today || todayIso()), [today]);
  const months = useMemo(() => monthsInWindow(range), [range]);
  const [state, setState] = useState({ status: "loading", duties: [] });
  const [attempt, setAttempt] = useState(0);
  const [month, setMonth] = useState(null);
  const [selected, setSelected] = useState(() => [...PrepaymentFormData.data.vacancies]);
  const [dropped, setDropped] = useState(0);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const { crm, crmuf } = PrepaymentFormData.data;

  // Uma busca só, da janela inteira permitida (mesma chamada de hoje): a lista nunca começa vazia.
  useEffect(() => {
    let alive = true;
    setState({ status: "loading", duties: [] });
    Promise.resolve()
      .then(() => getMedicalDutys({ startDate: range.start, endDate: range.end, crm, crmuf }))
      .then(
        (response) => {
          if (!alive) return;
          const all = response?.data?.data || [];
          const duties = onlyConcluded ? all.filter((d) => d.isConcluded !== false) : all;
          // Reconcilia a seleção com a busca nova: o que não voltou (ou não é mais selecionável) sai, com aviso.
          const selectable = splitByWindow(duties, range).inside.filter(isDutyAvailable);
          const cur = selectedRef.current;
          const kept = cur.filter((s) => selectable.some((x) => x.id === s.id));
          setDropped(cur.length - kept.length);
          if (kept.length !== cur.length) setSelected(kept);
          setState({ status: "ok", duties });
        },
        () => alive && setState({ status: "error", duties: [] })
      );
    return () => {
      alive = false;
    };
  }, [range, crm, crmuf, attempt, onlyConcluded]);

  useEffect(() => {
    PrepaymentFormData.setDutys(selected);
    setDisabledNext(selected.length === 0);
  }, [selected, setDisabledNext]);

  const { inside, before, after } = useMemo(() => splitByWindow(state.duties, range), [state.duties, range]);
  const activeMonth = month || defaultMonth(months, inside);
  const bounds = monthBounds(activeMonth, range);
  const list = dutiesInMonth(inside, activeMonth);
  const weeks = groupByWeek(list, bounds);
  const isSelected = (duty) => selected.some((s) => s.id === duty.id);
  const toggle = (duty) => {
    if (!isDutyAvailable(duty)) return;
    setSelected((cur) => (cur.some((s) => s.id === duty.id) ? cur.filter((s) => s.id !== duty.id) : [...cur, duty]));
  };
  const available = list.filter(isDutyAvailable);
  const allOn = available.length > 0 && available.every(isSelected);
  const someOn = available.some(isSelected);
  const toggleAll = () =>
    setSelected((cur) =>
      allOn ? cur.filter((s) => !available.some((a) => a.id === s.id)) : [...cur, ...available.filter((a) => !cur.some((s) => s.id === a.id))]
    );
  const otherMonth = selected.filter((s) => monthOf(s) !== activeMonth);

  const checkboxFor = (duty, disabled = !isDutyAvailable(duty)) => (
    <Checkbox
      checked={isSelected(duty)}
      disabled={disabled}
      onChange={() => toggle(duty)}
      inputProps={{ "aria-label": `${duty.hospital}, ${shortDate(String(duty.date))}` }}
      sx={{ mt: -0.5 }}
    />
  );

  const fixCrm = (
    <li>
      Confira seu CRM:{" "}
      <b style={{ color: "#1f2937" }}>
        {crm} / {crmuf}
      </b>{" "}
      —{" "}
      <Button
        variant="text"
        size="small"
        onClick={() => setActiveStep(2)}
        sx={{ p: 0, minWidth: 0, textTransform: "none", textDecoration: "underline", verticalAlign: "baseline", fontSize: "inherit" }}
      >
        corrigir
      </Button>
    </li>
  );

  return (
    <Box>
      <Chip label="Plantões de antecipação" sx={{ bgcolor: "#70e3bb", color: "#0f2a3a", fontWeight: 700, fontSize: 14, height: 32, px: 0.5 }} />
      <SectionLabel sx={{ mt: 3 }}>De qual mês são os plantões?</SectionLabel>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.5 }}>
        Mostramos os meses dos últimos 90 dias. Você pode marcar plantões de mais de um mês.
      </Typography>
      {onlyConcluded && <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.5 }}>{ONLY_CONCLUDED_HINT}</Typography>}

      {state.status === "ok" && dropped > 0 && (
        <Alert severity="info" sx={{ mt: 2 }} onClose={() => setDropped(0)}>
          {dropped === 1
            ? "1 plantão escolhido antes não está mais disponível e saiu da seleção."
            : `${dropped} plantões escolhidos antes não estão mais disponíveis e saíram da seleção.`}
        </Alert>
      )}

      {state.status === "loading" && (
        <Box role="status" aria-label="Buscando seus plantões" sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 1 }}>Buscando seus plantões dos últimos 90 dias…</Typography>
          <Skeleton variant="rounded" height={72} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={160} />
        </Box>
      )}

      {state.status === "error" && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setAttempt((a) => a + 1)} sx={{ textTransform: "none" }}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível buscar seus plantões. Verifique a conexão e tente de novo.
        </Alert>
      )}

      {state.status === "ok" && inside.length === 0 && before.length === 0 && after.length === 0 && (
        <Paper variant="outlined" sx={{ mt: 2, borderRadius: 2, textAlign: "center", py: 3.5, px: 2 }}>
          <Box sx={{ width: 56, height: 56, mx: "auto", borderRadius: "50%", bgcolor: "#eef2f6", display: "grid", placeItems: "center" }}>
            <SearchOffIcon sx={{ color: "#6b7280" }} aria-hidden />
          </Box>
          <Typography sx={{ mt: 1.5, fontWeight: 700, fontSize: 15.5 }}>Nenhum plantão realizado nos últimos 90 dias</Typography>
          <Box component="ul" sx={{ textAlign: "left", display: "inline-block", m: 0, mt: 1, pl: 2.5, fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
            <li>Plantões já enviados para antecipação ou com pagamento à vista não aparecem aqui.</li>
            {fixCrm}
          </Box>
        </Paper>
      )}

      {state.status === "ok" && (inside.length > 0 || before.length > 0 || after.length > 0) && (
        <>
          <Box
            role="radiogroup"
            aria-label="Mês"
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: mobile ? "repeat(2, minmax(0, 1fr))" : `repeat(${months.length}, minmax(0, 1fr))`,
              gap: 1,
            }}
          >
            {months.map((ym) => {
              const on = ym === activeMonth;
              const n = countAvailable(dutiesInMonth(inside, ym));
              const marked = selected.filter((s) => monthOf(s) === ym).length;
              const b = monthBounds(ym, range);
              const note = ym === range.end.slice(0, 7) ? `até ${shortDate(range.end)}` : ym === range.start.slice(0, 7) ? `a partir de ${shortDate(b.start)}` : "mês inteiro";
              return (
                <Box
                  key={ym}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setMonth(ym)}
                  sx={{
                    all: "unset",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    p: 1.25,
                    minWidth: 0,
                    borderRadius: 2,
                    border: `1.5px solid ${on ? "#17384c" : "#d6dde5"}`,
                    bgcolor: on ? "#17384c" : "#fff",
                    color: on ? "#fff" : "#1f2937",
                    "&:focus-visible": { boxShadow: "0 0 0 2px #05bbe5" },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{monthShort(ym)}</Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{note}</Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, mt: 0.5, color: on ? "#7fdcf2" : n ? "#0592b5" : "#9ca3af" }}>
                    {n ? plantoes(n) : "sem plantões"}
                  </Typography>
                  {marked > 0 && (
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: on ? "#a7f3c7" : "#1e6b34" }}>{`✓ ${marked} marcado${marked > 1 ? "s" : ""}`}</Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 3, mb: 1, flexWrap: "wrap" }}>
            <SectionLabel>
              {`Plantões de ${monthName(activeMonth)} `}
              <Box component="span" sx={{ color: "text.secondary", fontWeight: 500, fontSize: 14 }}>
                ({list.length})
              </Box>
            </SectionLabel>
            {available.length > 0 && (
              <FormControlLabel
                sx={{ mr: 0 }}
                control={<Checkbox checked={allOn} indeterminate={someOn && !allOn} onChange={toggleAll} />}
                label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>{`Selecionar todos de ${monthName(activeMonth)} (${available.length})`}</Typography>}
              />
            )}
          </Box>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            {list.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 3, px: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  {`Nenhum plantão realizado em ${monthName(activeMonth)} (de ${shortDate(bounds.start)} a ${shortDate(bounds.end)})`}
                </Typography>
                <Box component="ul" sx={{ textAlign: "left", display: "inline-block", m: 0, mt: 1, pl: 2.5, fontSize: 13.5, color: "text.secondary" }}>
                  <li>Plantões já enviados para antecipação ou com pagamento à vista não aparecem aqui.</li>
                  {fixCrm}
                </Box>
              </Box>
            ) : (
              weeks.map((week, w) => (
                <Box key={week.weekStart}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      bgcolor: "#f1f4f8",
                      borderTop: w ? "1px solid #e5e7eb" : "none",
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: "#475569",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <span>{`Semana de ${shortDate(week.weekStart)} a ${shortDate(week.weekEnd)}`}</span>
                    <span>{plantoes(week.items.length)}</span>
                  </Box>
                  {week.items.map((duty) => (
                    <Box key={duty.id} sx={{ borderTop: "1px solid #eceef1", bgcolor: isSelected(duty) ? "#f0f9fc" : "transparent" }}>
                      <DutyLine dateFirst duty={duty} mobile={mobile} action={checkboxFor(duty)} note={isDutyAvailable(duty) ? undefined : UNAVAILABLE} />
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Paper>

          {after.length > 0 && <OutOfRange title="Ainda não realizados" duties={after} mobile={mobile} checkboxFor={checkboxFor} />}
          {before.length > 0 && <OutOfRange title="Anteriores" duties={before} mobile={mobile} checkboxFor={checkboxFor} />}
        </>
      )}

      {selected.length > 0 && (
        <Box
          role="status"
          aria-label="Seleção"
          sx={{
            position: "sticky",
            bottom: 8,
            zIndex: 1,
            mt: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#17384c",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
              {`${selected.length} ${selected.length === 1 ? "plantão selecionado" : "plantões selecionados"} · ${formatBRL(sumValues(selected))}`}
            </Typography>
            {otherMonth.length > 0 && (
              <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>
                {`${otherMonth.length} de outro mês (${otherMonth
                  .slice(0, 4)
                  .map((s) => shortDate(String(s.date)))
                  .join(", ")}${otherMonth.length > 4 ? "…" : ""}) ${otherMonth.length === 1 ? "continua" : "continuam"} na seleção.`}
              </Typography>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelected([])}
            sx={{ textTransform: "none", color: "#fff", borderColor: "rgba(255,255,255,.5)" }}
          >
            Limpar seleção
          </Button>
        </Box>
      )}
      <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 1 }}>* É o valor total sem considerar taxas nem descontos provenientes da empresa medica.</Typography>
    </Box>
  );
};

export default Step4;
